import BaseLayout from '../../components/layout/layout-with-sider/BaseLayout';
import {Card, Result, Tabs,} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {AppActions, useAppContext} from '../../context/AppContext';
import {tab} from '@testing-library/user-event/dist/tab';
import {useLocation} from 'react-router-dom';
import RunOperations from './RunOperations';
import FixModule from './FixModule';
import LMA from './LMA';
import SurveyAutomationTab from "./SurveyAutomationTab";
import XSAutomationTab from "../xs-automation/XSAutomationTab";
import {useUser} from '../../context/UserContext';
import {ACCESS_MODULES, getPermissionSet, hasPermission} from '../../helpers/PermissionHelper';
import UploadEmptyState from '../UploadEmptyState';
import {findComparisonSide} from '../../helpers/SmartPlanSelection';

export default function Run() {
    const [runType, setRunType] = useState('Check');
    const location = useLocation();
    const [initialFixId, setInitialFixId] = useState(null);

    // Results sayfasindan "Go to Fix" ile gelindiyse fix-module tab'ini ac.
    useEffect(() => {
        if (location?.state?.tab) {
            setRunType(location.state.tab);
            if (location.state.fixId) setInitialFixId(location.state.fixId);
        }
    }, [location?.state?.tab, location?.state?.fixId]);
    const { appContext, dispatchProject, selectedHistoryId, selection, resolveSelectionSide } = useAppContext();
    const {user} = useUser();
    const permSet = useMemo(() => getPermissionSet(user), [user?.data?.permissions, user?.data?.isAdmin]);

    const canCheck = hasPermission(permSet, ACCESS_MODULES.RUN_CHECK);
    const canCompare = hasPermission(permSet, ACCESS_MODULES.RUN_COMPARE);
    const canFix = hasPermission(permSet, ACCESS_MODULES.RUN_FIX);
    const canLma = hasPermission(permSet, ACCESS_MODULES.RUN_LMA);
    const canSurveyAuto = hasPermission(permSet, ACCESS_MODULES.RUN_SURVEY_AUTOMATION);
    const canXsAuto = hasPermission(permSet, ACCESS_MODULES.RUN_XS_AUTOMATION);

    const firstAllowedTab = canCheck
        ? 'Check'
        : canCompare
            ? 'Comparison'
            : canFix
                ? 'fix-module'
                : canLma
                    ? 'LMA'
                    : canSurveyAuto
                        ? 'structure-generate'
                        : canXsAuto
                            ? 'xs-automation'
                            : null;

    useEffect(() => {
        const isCurrentAllowed =
            (runType === 'Check' && canCheck) ||
            (runType === 'Comparison' && canCompare) ||
            (runType === 'fix-module' && canFix) ||
            (runType === 'LMA' && canLma) ||
            (runType === 'structure-generate' && canSurveyAuto) ||
            (runType === 'xs-automation' && canXsAuto);

        if (!isCurrentAllowed && firstAllowedTab) {
            setRunType(firstAllowedTab);
        }
    }, [canCheck, canCompare, canFix, canLma, canSurveyAuto, canXsAuto]);

    const selectFirst = () => {
        let projectId = null;
        let planId = null;
        let river = null;
        let survey = null
        if (appContext?.projects?.length > 0) {
            const project = appContext?.projects[0];
            projectId = project?.id
            const surveyFiles = project?.shapeFiles?.filter(x => x.hasSurveyData);
            if(surveyFiles?.length > 0) {
                survey = {...surveyFiles[0]}
            }

            if (project?.projectFiles?.length > 0) {
                const plan = project.projectFiles[0];
                planId = plan?.id
                if (plan?.rivers?.length > 0) {
                    river = plan.rivers[0];
                }
            }
        }

        const defaultSide = {
            projectId,
            planId,
            river: river ? {...river} : undefined,
            survey: survey ? {...survey} : undefined,
        };

        const _existBaseId = appContext?.selection?.base?.projectId
        const _existCmpId = appContext?.selection?.compare?.projectId
        const _existBase = appContext?.projects?.find(x => x.id === _existBaseId);
        const _existCmp = appContext?.projects?.find(x => x.id === _existCmpId);

        const appSelection = {
            base: (_existBaseId && _existBase) ? appContext?.selection?.base : defaultSide,
            compare: (_existCmpId && _existCmp) ? appContext?.selection?.compare : defaultSide,
        };

        dispatchProject({
            type: AppActions.SET_PROJECT_SELECTION,
            data: {
                selection: {
                    ...appSelection,
                },
            },
        });
    };

    useEffect(() => {
        selectFirst();
    }, []);

    // selectFirst() defaults both sides to the same plan, so entering Comparison
    // would otherwise show e.g. Corrected Effective on both sides until the user
    // clicks the base plan. Apply the smart pairing to that untouched default —
    // a compare plan the user picked themselves is left alone.
    useEffect(() => {
        if (runType !== 'Comparison') return;

        const base = resolveSelectionSide(selection?.base);
        if (!base?.plan) return;

        const compareRef = selection?.compare;
        const isUntouched =
            !compareRef?.planId ||
            (compareRef.projectId === selection?.base?.projectId &&
                compareRef.planId === selection?.base?.planId);
        if (!isUntouched) return;

        const compare = findComparisonSide({
            projects: appContext?.projects,
            baseProjectId: base.project?.id,
            basePlan: base.plan,
            baseRiver: selection?.base?.river,
        });
        if (!compare) return;

        dispatchProject({
            type: AppActions.SET_PROJECT_SELECTION,
            data: {
                selection: {...selection, compare},
            },
        });
    }, [runType, selection?.base?.projectId, selection?.base?.planId, appContext?.projects]);


    return (
        <div>
            {appContext?.projects?.length === 0 && (
                <UploadEmptyState message={'There are no Uploaded Projects!'} />
            )}
            {appContext?.projects?.length > 0 && (
                <BaseLayout>
                <Card
                        title={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    <Tabs
                                        onChange={(tab) => setRunType(tab)}
                                        tab={tab}
                                        type={'card'}
                                        activeKey={runType}
                                        hideAdd={true}
                                        size={'small'}
                                        moreIcon={false}
                                    >
                                        {canCheck && (
                                            <Tabs.TabPane
                                                tab={'Check'}
                                                key={'Check'}
                                            />
                                        )}
                                        {canCompare && (
                                            <Tabs.TabPane
                                                tab={'Comparison'}
                                                key={'Comparison'}
                                            />
                                        )}
                                        {canFix && (
                                            <Tabs.TabPane
                                                tab={'Fix Module'}
                                                key={'fix-module'}
                                            />
                                        )}
                                        {canLma && (
                                            <Tabs.TabPane
                                                tab={'Library Model Automation'}
                                                key={'LMA'}
                                            />
                                        )}
                                        {canSurveyAuto && (
                                            <Tabs.TabPane
                                                tab={'Survey Automation'}
                                                key={'structure-generate'}
                                            />
                                        )}
                                        {canXsAuto && (
                                            <Tabs.TabPane
                                                tab={'XS Automation'}
                                                key={'xs-automation'}
                                            />
                                        )}
                                    </Tabs>
                                </div>
                            </div>
                        }
                    >
                        {runType === 'Check' && canCheck && (
                            <RunOperations runType={runType} />
                        )}
                        {runType === 'Comparison' && canCompare && (
                            <RunOperations runType={runType} />
                        )}
                        {runType === 'fix-module' && canFix && (
                            <FixModule initialFixId={initialFixId} />
                        )}
                        {runType === 'LMA' && canLma && (
                            <LMA />
                        )}
                        {runType === 'structure-generate' && canSurveyAuto && (
                            <SurveyAutomationTab />
                        )}
                        {runType === 'xs-automation' && canXsAuto && (
                            <XSAutomationTab />
                        )}
                        {!firstAllowedTab && (
                            <Result
                                status={'warning'}
                                title={'No module permission'}
                                subTitle={'Please contact your administrator to enable Run modules for your account.'}
                            />
                        )}
                    </Card>
                </BaseLayout>
            )}
        </div>
    );
}
